import React, { createContext, useContext, useState, useEffect, useMemo, ReactNode } from 'react';

// Define the User type for authentication context
interface User {
  id: string;
  name: string;
  email: string;
  avatar?: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  login: (userData: User) => void;
  logout: () => void;
  isLoading: boolean;
  error: string | null;
}

const defaultContext: AuthContextType = {
  user: null,
  isAuthenticated: false,
  login: () => {},
  logout: () => {},
  isLoading: false,
  error: null
};

const AuthContext = createContext<AuthContextType>(defaultContext);

export const useAuth = () => useContext(AuthContext);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Check for existing authentication on load
  useEffect(() => {
    const checkAuth = async () => {
      try {
        // Check localStorage for saved user
        const savedUserJson = localStorage.getItem('typeRushUser');
        if (savedUserJson) {
          const savedUser = JSON.parse(savedUserJson);
          setUser(savedUser);
        }
      } catch (err) {
        console.error('Error restoring authentication:', err);
        // Clear potentially corrupted auth data
        localStorage.removeItem('typeRushUser');
        // Set error message for display purposes
        setError('Error restoring authentication session');
      } finally {
        setIsLoading(false);
      }
    };
    
    checkAuth();
  }, []);

  const login = (userData: User) => {
    setUser(userData);
    localStorage.setItem('typeRushUser', JSON.stringify(userData));
    // Clear any previous errors when successfully logging in
    if (error) setError(null);
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem('typeRushUser');
  };

  const contextValue = useMemo(() => ({
    user,
    isAuthenticated: !!user,
    login,
    logout,
    isLoading,
    error
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [user, isLoading, error]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};
